import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  clearPendingThreadDraft,
  createDraftId,
  listRecoverablePendingDrafts,
  listUnverifiedPendingDrafts,
  readPendingThreadDraft,
  savePendingThreadDraft,
  type PendingThreadDraft,
  type PendingThreadMetadata,
  type PendingThreadStatus,
  type SerializedChatMessage,
} from "@/lib/pendingThreadStore";
import {
  persistAndVerifyThread,
  resolvePersistPhase,
  type PersistPhase,
} from "@/services/threadPersistVerification";
import { threadNeedsAutoTitle } from "@/hooks/useChatThread";
import { SESSION_RESTORED_EVENT } from "@/lib/sessionExpiry";

const AUTO_RETRY_MS = 30_000;

export type RunSyncOptions = {
  phase?: PersistPhase;
};

export type PendingThreadPersistenceOptions = {
  userId: string | null | undefined;
  workspaceId: string | null | undefined;
  spaceContextId?: string | null;
  onVerified?: (threadId: string) => void;
  generateMetadata?: (threadId: string) => void;
  shouldAutoTitle?: (threadId: string) => boolean;
};

export function usePendingThreadPersistence(options: PendingThreadPersistenceOptions) {
  const { userId, workspaceId, spaceContextId, onVerified, generateMetadata, shouldAutoTitle } =
    options;
  const { toast } = useToast();
  const [status, setStatus] = useState<PendingThreadStatus | "idle">("idle");
  const [lastError, setLastError] = useState<string | null>(null);
  const activeDraftIdRef = useRef<string | null>(null);
  const activeThreadIdRef = useRef<string | null>(null);
  const metadataRef = useRef<PendingThreadMetadata | null>(null);
  const messagesRef = useRef<SerializedChatMessage[]>([]);
  const syncInFlightRef = useRef(false);
  const prevStatusRef = useRef<PendingThreadStatus | "idle">("idle");

  const ensureDraftId = useCallback((): string => {
    if (!activeDraftIdRef.current) {
      activeDraftIdRef.current = createDraftId();
    }
    return activeDraftIdRef.current;
  }, []);

  const writeLocalDraft = useCallback(
    (
      nextStatus: PendingThreadStatus,
      messages: SerializedChatMessage[],
      threadId?: string | null,
      error?: string | null
    ) => {
      if (!userId || !metadataRef.current) return;
      messagesRef.current = messages;
      if (threadId !== undefined) activeThreadIdRef.current = threadId;
      const draftId = ensureDraftId();
      const existing = readPendingThreadDraft(userId, draftId);
      const draft: PendingThreadDraft = {
        draftId,
        threadId: activeThreadIdRef.current,
        userId,
        workspaceId: workspaceId ?? null,
        messages,
        metadata: metadataRef.current,
        status: nextStatus,
        lastSyncAttemptAt: nextStatus === "syncing" ? new Date().toISOString() : null,
        lastError: error ?? lastError,
        createdAt: existing?.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      savePendingThreadDraft(draft);
      if (error !== undefined) setLastError(error);
      setStatus(nextStatus);
    },
    [userId, workspaceId, ensureDraftId, lastError]
  );

  const beginDraft = useCallback(
    (
      messages: SerializedChatMessage[],
      metadata: PendingThreadMetadata,
      threadId: string | null
    ) => {
      metadataRef.current = metadata;
      activeThreadIdRef.current = threadId;
      if (!activeDraftIdRef.current) {
        activeDraftIdRef.current = createDraftId();
      }
      writeLocalDraft("local_only", messages, threadId, null);
    },
    [writeLocalDraft]
  );

  const hasActiveDraft = useCallback((): boolean => {
    return metadataRef.current != null && messagesRef.current.length > 0;
  }, []);

  const updateLocalMessages = useCallback(
    (messages: SerializedChatMessage[]) => {
      if (!userId || !metadataRef.current) return;
      messagesRef.current = messages;
      // Preserve synced_partial / verified during streaming — demoting to local_only
      // falsely triggers the "Not saved" banner after a successful early DB write.
      const nextStatus: PendingThreadStatus =
        status === "idle"
          ? "local_only"
          : status === "verified"
            ? "synced_partial"
            : (status as PendingThreadStatus);
      writeLocalDraft(nextStatus, messages);
    },
    [userId, status, writeLocalDraft]
  );

  const clearDraft = useCallback(() => {
    if (userId && activeDraftIdRef.current) {
      clearPendingThreadDraft(userId, activeDraftIdRef.current);
    }
    activeDraftIdRef.current = null;
    metadataRef.current = null;
    messagesRef.current = [];
    setLastError(null);
    setStatus("idle");
    prevStatusRef.current = "idle";
  }, [userId]);

  /** Start a new send/session without deleting older unverified drafts from localStorage. */
  const resetDraftSession = useCallback(() => {
    activeDraftIdRef.current = null;
    metadataRef.current = null;
    messagesRef.current = [];
  }, []);

  const runSync = useCallback(
    async (options?: RunSyncOptions): Promise<string | null> => {
      const phase = options?.phase ?? resolvePersistPhase(messagesRef.current);
      if (!userId || !metadataRef.current || messagesRef.current.length === 0) {
        return activeThreadIdRef.current;
      }
      if (syncInFlightRef.current) return activeThreadIdRef.current;
      syncInFlightRef.current = true;
      setStatus("syncing");
      setLastError(null);
      writeLocalDraft("syncing", messagesRef.current, activeThreadIdRef.current, null);

      const meta = metadataRef.current;
      const result = await persistAndVerifyThread({
        threadId: activeThreadIdRef.current,
        userId,
        messages: messagesRef.current,
        linkedSpaceIds: meta.linkedSpaceIds,
        attachedGitHubRepos: meta.attachedGitHubRepos,
        contextMode: meta.contextMode,
        chatMode: meta.chatMode,
        agentId: meta.agentId,
        topicId: meta.topicId,
        workspaceId: workspaceId ?? null,
        spaceContextId: spaceContextId ?? null,
        phase,
      });

      syncInFlightRef.current = false;

      if (result.verified && result.threadId) {
        activeThreadIdRef.current = result.threadId;

        if (phase === "early") {
          writeLocalDraft("synced_partial", messagesRef.current, result.threadId, null);
          prevStatusRef.current = "synced_partial";
          return result.threadId;
        }

        if (
          result.thread &&
          messagesRef.current.length >= 2 &&
          threadNeedsAutoTitle(result.thread.title) &&
          shouldAutoTitle?.(result.threadId) !== false
        ) {
          generateMetadata?.(result.threadId);
        }
        clearDraft();
        setStatus("verified");
        prevStatusRef.current = "verified";
        toast({
          title: "Saved to database",
          description: "Your conversation is confirmed in the database.",
        });
        onVerified?.(result.threadId);
        setTimeout(() => setStatus("idle"), 1500);
        return result.threadId;
      }

      // IDs confirmed in DB but turn incomplete — content is saved; don't scare the user.
      if (result.syncedPartial && result.threadId) {
        activeThreadIdRef.current = result.threadId;
        writeLocalDraft("synced_partial", messagesRef.current, result.threadId, null);
        prevStatusRef.current = "synced_partial";
        return result.threadId;
      }

      const err = result.error ?? "Could not confirm save to database.";
      setLastError(err);
      if (result.threadId) activeThreadIdRef.current = result.threadId;
      writeLocalDraft("local_only", messagesRef.current, activeThreadIdRef.current, err);
      if (prevStatusRef.current !== "local_only") {
        toast({
          title: "Not saved to database yet",
          description:
            "Your messages are stored in browser local storage. We will retry automatically.",
          variant: "destructive",
        });
      }
      prevStatusRef.current = "local_only";
      return activeThreadIdRef.current;
    },
    [
      userId,
      workspaceId,
      spaceContextId,
      writeLocalDraft,
      clearDraft,
      toast,
      onVerified,
      generateMetadata,
      shouldAutoTitle,
    ]
  );

  const loadDraft = useCallback(
    (draft: PendingThreadDraft) => {
      activeDraftIdRef.current = draft.draftId;
      activeThreadIdRef.current = draft.threadId;
      metadataRef.current = draft.metadata;
      messagesRef.current = draft.messages;
      setLastError(draft.lastError);
      setStatus(draft.status === "verified" ? "idle" : draft.status);
      prevStatusRef.current = draft.status;
    },
    []
  );

  useEffect(() => {
    if (status !== "local_only") return;
    const timer = window.setInterval(() => {
      void runSync({ phase: resolvePersistPhase(messagesRef.current) });
    }, AUTO_RETRY_MS);
    return () => window.clearInterval(timer);
  }, [status, runSync]);

  /** Re-auth after a session-expiry prompt shouldn't make the user wait out the 30s poll —
   * retry immediately once the session is back. */
  useEffect(() => {
    const onSessionRestored = () => {
      if (status === "local_only") {
        void runSync({ phase: resolvePersistPhase(messagesRef.current) });
      }
    };
    window.addEventListener(SESSION_RESTORED_EVENT, onSessionRestored);
    return () => window.removeEventListener(SESSION_RESTORED_EVENT, onSessionRestored);
  }, [status, runSync]);

  useEffect(() => {
    const flush = () => {
      if (!userId || messagesRef.current.length === 0) return;
      writeLocalDraft(status === "idle" ? "local_only" : (status as PendingThreadStatus), messagesRef.current);
      if (status === "local_only") {
        void runSync({ phase: resolvePersistPhase(messagesRef.current) });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, status, writeLocalDraft, runSync]);

  return {
    status,
    lastError,
    activeDraftId: activeDraftIdRef.current,
    beginDraft,
    updateLocalMessages,
    runSync,
    clearDraft,
    resetDraftSession,
    loadDraft,
    hasActiveDraft,
    listUnverifiedDrafts: useCallback(
      () => (userId ? listUnverifiedPendingDrafts(userId) : []),
      [userId]
    ),
    listRecoverableDrafts: useCallback(
      () => (userId ? listRecoverablePendingDrafts(userId) : []),
      [userId]
    ),
  };
}
